import { Container, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";
import { getReferrerPath } from "../../../../../utilities/navigationHelper";
import DocumentActivityPageHeader from "../../common/document-activity/DocumentActivityPageHeader";
import DocumentActivitySummary from "../../common/document-activity/DocumentActivitySummary";
import TotalTimePerPageSection from "../../common/document-activity/TotalTimePerPageSection";
import UserDocumentActivitySection from "../../common/document-activity/UserDocumentActivitySection";

const getDocCollectionFileDownloadInfo = withErrorHandling(adminApi.getDocCollectionFileDownloadInfo);

const ContactDocumentActivityPage = () => {
  usePageTitle("Document Activity");

  const { clientCode } = useClientContext();
  const { id, postMessageRequestId, attachmentId } = useParams();
  const location = useLocation();

  const isDocumentActivityFetchDisabled = !id || !attachmentId;
  const getMessageAttachmentInfo = useCallback(
    () => adminApi.getMessageAttachmentInfo(defined(attachmentId), { contactId: defined(id) }),
    [attachmentId, id]
  );

  const [messageAttachmentInfo, fetchMessageAttachmentInfoError, { isFetching: isFetchingMessageAttachmentInfo }] =
    useFetch(getMessageAttachmentInfo, { disabled: isDocumentActivityFetchDisabled });

  const getAggregatedDocumentViews = useCallback(
    () => adminApi.getAggregatedDocumentViewsByContact(defined(attachmentId), defined(id)),
    [attachmentId, id]
  );

  const [
    agggregatedDocumentViews,
    fetchAggregatedDocumentViewsError,
    { isFetching: isFetchingAggregatedDocumentViews },
  ] = useFetch(getAggregatedDocumentViews, { disabled: isDocumentActivityFetchDisabled });

  const getAllDocumentViews = useCallback(
    () =>
      adminApi.searchDocumentViews({
        range: { skip: 0, take: 250, totals: false },
        contactId: defined(id),
        attachmentIds: [defined(attachmentId)],
      }),
    [attachmentId, id]
  );

  const [allDocumentViews, fetchAllDocumentViewsError, { isFetching: isFetchingAllDocumentViews }] = useFetch(
    getAllDocumentViews,
    { disabled: isDocumentActivityFetchDisabled }
  );

  const getFileDownloadInfo = useCallback(
    () => getDocCollectionFileDownloadInfo(defined(postMessageRequestId), defined(attachmentId)),
    [attachmentId, postMessageRequestId]
  );

  if (!id || !postMessageRequestId || !attachmentId) {
    return <Navigate to={`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.contacts}`} />;
  }

  const fetchError = fetchMessageAttachmentInfoError || fetchAggregatedDocumentViewsError || fetchAllDocumentViewsError;
  if (fetchError && fetchError.code !== 404) {
    logError(fetchError, "[ContactDocumentActivityPage]");
    return <DataLoadingFailed />;
  }

  if (isFetchingMessageAttachmentInfo || isFetchingAggregatedDocumentViews) {
    return <InlineLoader />;
  }

  const backPath =
    getReferrerPath(location.state) ??
    `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.contacts}/${id}/documents`;

  const documentViewsForAttachment = (agggregatedDocumentViews ?? []).find(
    (view) => view.attachmentId === attachmentId
  );

  return (
    <>
      <DocumentActivityPageHeader
        backPath={backPath}
        fileName={messageAttachmentInfo?.fileName}
        categoryId={messageAttachmentInfo?.categoryId}
        documentCollectionNo={messageAttachmentInfo?.documentCollectionNo}
        fundNames={messageAttachmentInfo?.fundNames ?? []}
        getFileDownloadInfo={getFileDownloadInfo}
      />
      <Container maxWidth="lg" sx={{ height: "100%" }}>
        <Stack spacing={2} sx={{ py: 2.5, width: "100%", height: "100%" }}>
          <Typography variant="h6" sx={{ pt: 2 }}>
            {messageAttachmentInfo?.contactName || "Contact"} activity
          </Typography>
          {documentViewsForAttachment && (
            <DocumentActivitySummary aggregatedDocumentViews={documentViewsForAttachment} />
          )}
          {documentViewsForAttachment && (
            <TotalTimePerPageSection aggregatedDocumentViews={documentViewsForAttachment} />
          )}
          {allDocumentViews && (
            <UserDocumentActivitySection
              documentViews={allDocumentViews?.items ?? []}
              loading={isFetchingAllDocumentViews}
              excludeColumns={["userInfo"]}
            />
          )}
        </Stack>
      </Container>
    </>
  );
};

export default ContactDocumentActivityPage;
